import { CommonActions, type NavigationAction } from "@react-navigation/native";
import type { Notification } from "@src/packages/care/useNotifications";
import { parseBadgeUnlockEvent } from "@src/packages/badges/badge-events";
import {
  getNotificationContract,
  getNotificationLinkType,
  parseNotificationData,
} from "./notification-contract";

/**
 * Resolve notification navigation outside the feed component so push taps,
 * feed taps, and future deep-link entry points share exactly one route map.
 */
export function getNotificationNavAction(
  notification: Notification,
): NavigationAction | null {
  const badgeEvent = parseBadgeUnlockEvent(notification);
  if (badgeEvent) {
    return CommonActions.navigate("Profile", {
      screen: "YouOverview",
      params: { badgeId: badgeEvent.event.badgeId },
    });
  }

  const data = parseNotificationData(notification.data);
  const linkType = getNotificationLinkType(notification.data);
  // Producers may use the generic linkId or the typed identifier that matches
  // the link kind. Normalize both forms before selecting a destination.
  const linkId =
    data.linkId ??
    data.medicationId ??
    data.appointmentId ??
    data.documentId ??
    data.requestId ??
    data.resultId ??
    data.clinicId ??
    data.cycleId ??
    data.partnerPatientId;

  switch (linkType) {
    case "assistant":
    case "texting_agent":
      return CommonActions.navigate("Home", {
        screen: "Chat",
        params: { screen: "Assistant" },
      });

    case "medical_history":
      return CommonActions.navigate("Home", {
        screen: "Care",
        params: { screen: "MedicalHistory" },
      });

    case "medication":
      return CommonActions.navigate("Home", {
        screen: "Learn",
        params: {
          screen: linkId ? "MedicationDetail" : "Medications",
          ...(linkId ? { params: { medicationId: linkId } } : {}),
        },
      });

    case "appointment":
      return CommonActions.navigate("Home", {
        screen: "Care",
        params: {
          screen: "CareOverview",
          params: linkId ? { appointmentId: linkId } : undefined,
        },
      });

    case "document":
      return CommonActions.navigate("Home", {
        screen: "Care",
        params: {
          screen: "Documents",
          params: {
            ...(linkId ? { initialDocumentId: linkId } : {}),
          },
        },
      });

    case "document_request":
      return CommonActions.navigate("Home", {
        screen: "Care",
        params: {
          screen: "Documents",
          params: {
            initialDocumentFilter: "requested",
            ...(linkId ? { initialDocumentId: linkId } : {}),
          },
        },
      });

    case "test_result":
    case "lab_result":
      return CommonActions.navigate("Home", {
        screen: "Care",
        params: {
          screen: "AllLabResults",
          params: {
            ...(linkId ? { initialResultId: linkId } : {}),
          },
        },
      });

    case "clinic":
      return CommonActions.navigate("Home", {
        screen: "Access",
        params: {
          screen: linkId ? "ClinicDetail" : "FindCare",
          ...(linkId ? { params: { clinicId: linkId } } : {}),
        },
      });

    case "coverage":
      return CommonActions.navigate("Profile", {
        screen: "CoverageBenefitsDetail",
      });

    case "payment":
      return CommonActions.navigate("Home", {
        screen: "Costs",
        params: { screen: "Payments" },
      });

    case "cycle":
      return CommonActions.navigate("Home", {
        screen: "Care",
        params: {
          screen: linkId ? "CycleDetail" : "CareOverview",
          ...(linkId ? { params: { cycleId: linkId } } : {}),
        },
      });

    case "partner":
      // Relationship notifications do not carry reusable invite codes or
      // clinical deep-link authority. Open the durable Connections surface,
      // where the current invitation/relationship state is fetched again.
      return CommonActions.navigate("Profile", {
        screen: "ConnectionsDetail",
      });

    case "badge":
      return null;

    default:
      break;
  }

  switch (getNotificationContract(notification.type).destination) {
    case "assistant":
      return CommonActions.navigate("Home", {
        screen: "Chat",
        params: { screen: "Assistant" },
      });
    case "medication":
      return CommonActions.navigate("Home", {
        screen: "Learn",
        params: { screen: "Medications" },
      });
    case "appointment":
    case "cycle":
      return CommonActions.navigate("Home", {
        screen: "Care",
        params: { screen: "CareOverview" },
      });
    case "document":
    case "document_request":
      return CommonActions.navigate("Home", {
        screen: "Care",
        params: { screen: "Documents" },
      });
    case "test_result":
      return CommonActions.navigate("Home", {
        screen: "Care",
        params: { screen: "AllLabResults" },
      });
    case "clinic":
      return CommonActions.navigate("Home", {
        screen: "Access",
        params: { screen: "FindCare" },
      });
    case "coverage":
      return CommonActions.navigate("Profile", {
        screen: "CoverageBenefitsDetail",
      });
    case "payment":
      return CommonActions.navigate("Home", {
        screen: "Costs",
        params: { screen: "Payments" },
      });
    case "partner":
      return CommonActions.navigate("Profile", {
        screen: "YouOverview",
      });
    case "badge":
    case "none":
    case "typed":
    default:
      return null;
  }
}

export function getNotificationNavActionForPayload(payload: {
  type?: unknown;
  data?: unknown;
}): NavigationAction | null {
  const rawPayload =
    payload.data &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data)
      ? (payload.data as Record<string, unknown>)
      : {};
  const nestedData =
    rawPayload.data && typeof rawPayload.data === "object"
      ? rawPayload.data
      : rawPayload;
  const type =
    typeof payload.type === "string"
      ? payload.type
      : typeof rawPayload.type === "string"
        ? rawPayload.type
        : "system";

  if (
    type === "badge_unlocked" &&
    nestedData &&
    typeof nestedData === "object" &&
    typeof (nestedData as Record<string, unknown>).badgeId === "string"
  ) {
    return CommonActions.navigate("Profile", {
      screen: "YouOverview",
      params: { badgeId: (nestedData as Record<string, string>).badgeId },
    });
  }

  return getNotificationNavAction({
    type,
    data: nestedData,
  } as Notification);
}
